import React from "react";
import { CheckCircle, ChevronRight } from "lucide-react";
import { motion } from "framer-motion";
import Layout from "../Layout";
import SEOHead from "../SEOHead";

const QualityPage = () => {
  return (
    <Layout>
      <SEOHead
        title="High Quality PEB Structures & Steel Fabrication – TR Structures Hyderabad"
        description="TR Structures delivers premium PEB structures and steel fabrication in Hyderabad with world-class quality control. Earthquake-resistant, eco-friendly, and fully customizable industrial & warehouse buildings."
        keywords="PEB structures Hyderabad, steel fabrication quality, pre-engineered buildings, industrial construction Hyderabad, warehouse steel buildings, TR Structures"
      />

      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section
          className="relative pt-32 pb-20 bg-cover bg-center text-white text-center"
          style={{
            backgroundImage: `linear-gradient(rgba(92, 129, 194, 0.92), rgba(53, 66, 87, 0.92)), url('https://media.istockphoto.com/id/1204732594/photo/industrial-technology-concept-communication-network-industry-4-0-factory-automation.webp?a=1&b=1&s=612x612&w=0&k=20&c=dwlc2hGJy40Te9R6vh2DldVWx65hgyAaHFXL4AYGElQ=')`,
          }}
        >
          <div className="container-custom">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-5xl md:text-5xl lg:text-4xl font-extrabold mb-8"
            >
              Our Quality Process
            </motion.h1>
            <div className="flex items-center justify-center gap-4 text-lg">
              <a href="/" className="text-secondary font-semibold hover:underline">
                Home
              </a>
              <ChevronRight className="w-5 h-5" />
              <span className="text-white/80">Quality Process</span>
            </div>
          </div>
        </section>

        {/* High Quality Service Section */}
        <section className="section-padding bg-muted/30">
          <div className="container-custom">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              {/* Content - Mobile First */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="order-1 lg:order-2"
              >
                <span className="text-secondary font-bold uppercase tracking-wider text-sm block mb-4">
                  Excellence in Every Detail
                </span>
                <h2 className="text-4xl md:text-5xl font-extrabold text-foreground mb-6 leading-tight">
                  A Leading PEB Manufacturer with World-Class Quality Standards
                </h2>
                <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                  At <strong className="text-primary">TR Structures</strong>, quality is not just a promise — it's our foundation. We follow international standards in design, fabrication, and installation to deliver structures that stand the test of time.
                </p>
                <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                  Every component undergoes rigorous testing — from raw material inspection to final erection — ensuring 100% compliance, safety, and durability.
                </p>

                <ul className="space-y-5">
                  {[
                    "100% Quality Control & Satisfaction Guarantee",
                    "Highly Experienced & Certified Engineering Team",
                    "Advanced Testing & Inspection at Every Stage",
                    "Eco-Friendly & Sustainable Construction Practices",
                    "Unmatched Workmanship & Precision Engineering",
                  ].map((item, i) => (
                    <motion.li
                      key={i}
                      initial={{ opacity: 0, x: -30 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.1 }}
                      className="flex items-start gap-4 text-foreground"
                    >
                      <CheckCircle className="w-7 h-7 text-secondary flex-shrink-0 mt-1" />
                      <span className="text-lg leading-relaxed">{item}</span>
                    </motion.li>
                  ))}
                </ul>
              </motion.div>

              {/* Image */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="order-2 lg:order-1"
              >
                <img
                  src="https://media.istockphoto.com/id/1052976686/photo/a-futuristic-architect-businessman-industry-4-0-engineer-manager-using-mobile-smartphone-with.webp?a=1&b=1&s=612x612&w=0&k=20&c=_SGuFquaM79po6p64Hh6RImVQ01GlO9qsRnp-vyKEZg="
                  alt="Quality Inspection & Testing - TR Structures"
                  className="w-full rounded-3xl shadow-2xl"
                />
              </motion.div>
            </div>
          </div>
        </section>

        {/* Quality Process Cards */}
        <section className="section-padding bg-white">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl md:text-5xl font-extrabold text-foreground mb-6">
                Our Quality Process
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                From raw material to final delivery — quality is monitored at every step
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-10">
              {[
                {
                  title: "Quality Assurance",
                  desc: "Proactive quality management system ensuring excellence from design to delivery with continuous improvement.",
                  img: "https://media.istockphoto.com/id/864483044/photo/wind-power-plant-renewable-energy-concept.webp?a=1&b=1&s=612x612&w=0&k=20&c=jgQxqKLJiyUc8LThq9DmDuhAWYViRg5rbYsf5h75hl8=",
                },
                {
                  title: "Quality Control",
                  desc: "Stringent inspection of raw materials, welding, painting, and assembly with documented test reports.",
                  img: "https://media.istockphoto.com/id/1162658079/photo/ladder-career-path-for-business-growth-success-process-concept-hand-arranging-wood-block.webp?a=1&b=1&s=612x612&w=0&k=20&c=GFc3tI5zuibxGlDmgMtYjhxm8jmK-cfk1sIRUkzuOog=",
                },
                {
                  title: "Quality Planning",
                  desc: "Detailed project-specific quality plans aligned with IS codes, client requirements, and global standards.",
                  img: "https://media.istockphoto.com/id/2166587220/photo/engineer-team-brainstorm-meeting-in-factory-office-presentation-on-esg-project-planning.webp?a=1&b=1&s=612x612&w=0&k=20&c=sJPYjxgnpGnRpCKwPk3WYV2Xy-Y6POzhIs1FoeU8yCk=",
                },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.2 }}
                  whileHover={{ y: -10 }}
                  className="bg-card rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300"
                >
                  <img
                    src={item.img + "&auto=format&fit=crop"}
                    alt={item.title}
                    className="w-full h-64 object-cover"
                  />
                  <div className="p-8">
                    <h3 className="text-2xl font-bold text-foreground mb-4">{item.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">{item.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Request Quote CTA */}
        <section className="py-24 bg-cover bg-center relative"
  style={{
    backgroundImage: `linear-gradient(rgba(11, 38, 83, 0.95), rgba(11, 38, 83, 0.95)), url('https://images.unsplash.com/photo-1503387762-592deb58ef4e?q=80&w=2070&auto=format&fit=crop')`,
  }}
>
  <div className="container-custom text-center">
    <motion.h3
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      className="text-4xl md:text-5xl font-extrabold text-white mb-8"
    >
      Ready for World-Class Quality?
    </motion.h3>

    <motion.button
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      onClick={() => window.location.href = "/contact"}
      className="bg-secondary text-white px-12 py-5 rounded-full text-xl font-bold shadow-2xl hover:bg-secondary/90 transition-all"
    >
      Enquiry Here
    </motion.button>
  </div>
</section>

      </div>
    </Layout>
  );
};

export default QualityPage;